import { useState, useEffect, useCallback } from 'react'
import {
  apiFriendList,
  apiFriendRespond,
  apiFriendDelete,
  apiFriendBlock,
  FriendInfo,
  FriendRequestInfo,
} from '../server/ServiceCall'
import '../css/Friend.css'

interface Props {
  token:     string
  myCode:    number
  onOpenSearch: () => void
}

type Tab = 'friends' | 'received' | 'sent'

export default function FriendListPanel({ token, myCode, onOpenSearch }: Props) {
  const [tab,        setTab]        = useState<Tab>('friends')
  const [friends,    setFriends]    = useState<FriendInfo[]>([])
  const [received,   setReceived]   = useState<FriendRequestInfo[]>([])
  const [sent,       setSent]       = useState<{ toUserCode: number; toNickname: string; requestedAt: string }[]>([])
  const [loading,    setLoading]    = useState(false)
  const [toast,      setToast]      = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await apiFriendList(token)
    setLoading(false)
    if (!data) return
    setFriends(data.friends)
    setReceived(data.received)
    setSent(data.sent)
  }, [token])

  useEffect(() => { load() }, [load])

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  async function handleRespond(fromUserCode: number, action: 'ACCEPT' | 'REJECT') {
    const res = await apiFriendRespond(token, fromUserCode, action)
    showToast(res.resultMessage)
    if (res.resultCode === '00') load()
  }

  async function handleDelete(targetCode: number, nickname: string) {
    if (!window.confirm(`${nickname} 님을 친구 목록에서 삭제하시겠습니까?`)) return
    const res = await apiFriendDelete(token, targetCode)
    showToast(res.resultMessage)
    if (res.resultCode === '00') load()
  }

  async function handleBlock(targetCode: number, nickname: string) {
    if (!window.confirm(`${nickname} 님을 차단하시겠습니까?`)) return
    const res = await apiFriendBlock(token, targetCode, 'BLOCK')
    showToast(res.resultMessage)
    if (res.resultCode === '00') load()
  }

  const receivedCount = received.length
  const sentCount     = sent.length

  return (
    <div className="friend-panel">
      {toast && <div className="friend-toast">{toast}</div>}

      <div className="friend-header">
        <span className="friend-title">친구</span>
        <button className="friend-search-btn" onClick={onOpenSearch} title="유저 검색">🔍 검색</button>
      </div>

      <div className="friend-tabs">
        <button
          className={tab === 'friends' ? 'ftab active' : 'ftab'}
          onClick={() => setTab('friends')}
        >
          친구 <span className="badge">{friends.length}</span>
        </button>
        <button
          className={tab === 'received' ? 'ftab active' : 'ftab'}
          onClick={() => setTab('received')}
        >
          받은 요청 {receivedCount > 0 && <span className="badge red">{receivedCount}</span>}
        </button>
        <button
          className={tab === 'sent' ? 'ftab active' : 'ftab'}
          onClick={() => setTab('sent')}
        >
          보낸 요청 {sentCount > 0 && <span className="badge">{sentCount}</span>}
        </button>
      </div>

      {loading && <div className="friend-loading">불러오는 중...</div>}

      {/* 친구 목록 */}
      {tab === 'friends' && !loading && (
        <div className="friend-list">
          {friends.length === 0 && <div className="friend-empty">친구가 없습니다.</div>}
          {friends.map(f => (
            <div key={f.userCode} className="friend-item">
              <div className="friend-avatar">
                {f.nickname.charAt(0).toUpperCase()}
              </div>
              <div className="friend-info">
                <span className="friend-name">{f.nickname}</span>
                <span className="friend-sub">
                  {f.lastLoginAt ? `최근 접속: ${f.lastLoginAt.slice(0, 10)}` : '접속 기록 없음'}
                </span>
              </div>
              <div className="friend-actions">
                <button
                  className="faction-btn danger"
                  title="차단"
                  onClick={() => handleBlock(f.userCode, f.nickname)}
                >차단</button>
                <button
                  className="faction-btn del"
                  title="친구 삭제"
                  onClick={() => handleDelete(f.userCode, f.nickname)}
                >삭제</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 받은 요청 */}
      {tab === 'received' && !loading && (
        <div className="friend-list">
          {received.length === 0 && <div className="friend-empty">받은 요청이 없습니다.</div>}
          {received.map(r => (
            <div key={r.fromUserCode} className="friend-item">
              <div className="friend-avatar req">{r.fromNickname.charAt(0).toUpperCase()}</div>
              <div className="friend-info">
                <span className="friend-name">{r.fromNickname}</span>
                <span className="friend-sub">{r.requestedAt.slice(0, 10)} 요청</span>
              </div>
              <div className="friend-actions">
                <button
                  className="faction-btn accept"
                  onClick={() => handleRespond(r.fromUserCode, 'ACCEPT')}
                >수락</button>
                <button
                  className="faction-btn reject"
                  onClick={() => handleRespond(r.fromUserCode, 'REJECT')}
                >거부</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 보낸 요청 */}
      {tab === 'sent' && !loading && (
        <div className="friend-list">
          {sent.length === 0 && <div className="friend-empty">보낸 요청이 없습니다.</div>}
          {sent.map(s => (
            <div key={s.toUserCode} className="friend-item">
              <div className="friend-avatar sent">{s.toNickname.charAt(0).toUpperCase()}</div>
              <div className="friend-info">
                <span className="friend-name">{s.toNickname}</span>
                <span className="friend-sub">{s.requestedAt.slice(0, 10)} 요청 보냄 · 대기중</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
